import { useTranslation } from 'react-i18next'
import Button from '../../../ui/Button'
import Menu, { MenuItem } from '../../../ui/Menu'
import Tag from '../../../ui/Tag'
import { CloseIcon, EditIcon, RestartIcon, TrashIcon } from '../../../ui/icons'
import { checkProjectRole } from '../../../utils'
import { Journey, Project } from '../../../types'

interface JourneyEditorActionsProps {
    journey: Journey
    project: Project
    isDeleted: boolean
    isDraft: boolean
    parentId?: number
    draftId?: number
    saving: boolean
    publishing: boolean
    hasUnsavedChanges: boolean
    setEditOpen: (open: boolean) => void
    saveSteps: () => Promise<void>
    publishJourney: () => Promise<void>
    restoreJourney: () => Promise<void>
    createDraft: () => Promise<void>
    editDraft: (id: number) => void
    closeDraft: () => void
    deleteDraft: () => Promise<void>
}

export function JourneyEditorActions({
    journey,
    project,
    isDeleted,
    isDraft,
    parentId,
    draftId,
    saving,
    publishing,
    hasUnsavedChanges,
    setEditOpen,
    saveSteps,
    publishJourney,
    restoreJourney,
    createDraft,
    editDraft,
    closeDraft,
    deleteDraft,
}: JourneyEditorActionsProps) {
    const { t } = useTranslation()

    if (isDeleted) {
        return (
            <>
                <Tag variant="error" size="large">
                    {t('journey_archived')}
                </Tag>
                {!parentId && checkProjectRole('publisher', project.role) && (
                    <Button icon={<RestartIcon />} onClick={restoreJourney}>
                        {t('journey_restore')}
                    </Button>
                )}
            </>
        )
    }

    if (isDraft) {
        return (
            <>
                {hasUnsavedChanges
                    ? <Button
                        onClick={saveSteps}
                        isLoading={saving}
                        variant="primary"
                    >
                        {t('journey_draft_save')}
                    </Button>
                    : checkProjectRole('publisher', project.role) && (
                        <Button
                            onClick={publishJourney}
                            isLoading={publishing}
                        >
                            {t('publish')}
                        </Button>
                    )
                }
                <Menu size="regular">
                    {!parentId && (<MenuItem icon={<EditIcon />} onClick={() => setEditOpen(true)}>
                        {t('edit_details')}
                    </MenuItem>)}
                    <MenuItem icon={<CloseIcon />} onClick={closeDraft}>
                        {t('journey_draft_close')}
                    </MenuItem>
                    <MenuItem icon={<TrashIcon />} onClick={deleteDraft}>
                        {t('journey_draft_delete')}
                    </MenuItem>
                </Menu>
            </>
        )
    }

    return (
        <>
            <Tag
                variant={journey.status === 'live' ? 'success' : 'plain'}
                size="large"
            >
                {t(journey.status)}
            </Tag>
            <Button
                variant="secondary"
                onClick={() => setEditOpen(true)}
            >
                {t('edit_details')}
            </Button>
            {draftId
                ? (
                    <Button
                        onClick={() => editDraft(draftId)}
                        variant="primary"
                    >
                        {t('journey_draft_edit')}
                    </Button>
                )
                : (
                    <Button
                        onClick={createDraft}
                        isLoading={saving}
                        variant="primary"
                    >
                        {t('journey_draft_create')}
                    </Button>
                )}
        </>
    )
}
